import { useState, useRef, useEffect, useMemo } from 'react';
import './BlogsPage.css';
import { Header } from '@shared/components/Header';
import { BlogFeed, useBlogFeed } from '@features/blog-feed';
import { CategoryTabs, useCategories } from '@features/category-navigation';
import { SubCategoryTabs } from '@features/perspective-selection';
import type { SubCategoryInfo } from '@features/category-navigation';

const ALL_SUBCATEGORY: SubCategoryInfo = { slug: 'all', name: 'All', sortOrder: -1 };

export function BlogsPage() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('all');
  const scrollRef = useRef<HTMLDivElement>(null);

  const { categories } = useCategories('Blog');

  // Auto-select first category when loaded
  useEffect(() => {
    if (categories.length > 0 && activeCategory === null) {
      setActiveCategory(categories[0].slug);
    }
  }, [categories, activeCategory]);

  const activeSubCategories = useMemo(() => {
    const cat = categories.find(c => c.slug === activeCategory);
    return cat?.subCategories ?? [];
  }, [categories, activeCategory]);

  const subCategoriesWithAll = useMemo(() => {
    return [ALL_SUBCATEGORY, ...activeSubCategories];
  }, [activeSubCategories]);

  // Convert "all" to null for the API
  const feedSubCategory = selectedSubCategory === 'all' ? null : selectedSubCategory;

  const { items, isLoading, isFetchingMore, error, hasMore, fetchMore, retry } =
    useBlogFeed(feedSubCategory);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setSelectedSubCategory('all');
    // Scroll back to top so header is visible when changing category
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="blogs-page" ref={scrollRef}>
      <div className="blogs-page__header">
        <Header />
        <CategoryTabs
          categories={categories}
          activeCategory={activeCategory ?? ''}
          onCategoryChange={handleCategoryChange}
        />
        {activeSubCategories.length > 0 && (
          <div className="container">
            <SubCategoryTabs
              subCategories={subCategoriesWithAll}
              selectedSubCategory={selectedSubCategory}
              onSubCategoryChange={setSelectedSubCategory}
            />
          </div>
        )}
      </div>

      <main id="main-content" className="blogs-page__feed">
        <BlogFeed
          items={items}
          isLoading={isLoading}
          isFetchingMore={isFetchingMore}
          error={error}
          hasMore={hasMore}
          fetchMore={fetchMore}
          retry={retry}
          scrollContainerRef={scrollRef}
        />
      </main>
    </div>
  );
}
